<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Loyverse POS Settings</h1>
        <div class="gl-breadcrumb">
          <router-link to="/">Home</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <router-link to="/owner/loyverse">POS Orders</router-link>
          <span class="gl-breadcrumb-sep">/</span>
          <span>Settings</span>
        </div>
      </div>
    </div>

    <!-- Status banner -->
    <div class="gl-status-banner" :class="settings.is_configured ? 'gl-banner-ok' : 'gl-banner-warn'">
      <div class="gl-banner-icon">
        <svg v-if="settings.is_configured" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      </div>
      <div>
        <span v-if="settings.is_configured">
          Integration active — <strong>{{ settings.mapped_stores }}</strong> store(s) mapped · <strong>{{ settings.today_orders }}</strong> orders received today
        </span>
        <span v-else>
          Integration not yet configured. Add at least one store mapping below to get started.
        </span>
      </div>
    </div>

    <div class="gl-settings-layout">
      <!-- Setup Guide -->
      <div class="gl-setup-card">
        <div class="gl-setup-header">
          <div class="gl-setup-icon">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </div>
          <h2 class="gl-setup-title">Setup Guide</h2>
        </div>

        <!-- Step 1 -->
        <div class="gl-step">
          <div class="gl-step-num">1</div>
          <div class="gl-step-body">
            <div class="gl-step-title">Configure Webhook in Loyverse</div>
            <ol class="gl-step-list">
              <li>Go to <a href="https://r.loyverse.com/dashboard/#/webhooks" target="_blank" class="gl-link">Loyverse Developer Dashboard ↗</a></li>
              <li>Click <strong>Create webhook</strong></li>
              <li>Select event: <strong>Receipt created</strong></li>
              <li>Enter the webhook URL below</li>
              <li>Set status to <strong>Enabled</strong> and click Save</li>
            </ol>

            <div class="gl-webhook-block">
              <div class="gl-webhook-label">Your Webhook URL</div>
              <div class="gl-webhook-row">
                <input type="text" class="gl-webhook-input" :value="webhookUrl" readonly ref="webhookInput">
                <button class="gl-copy-btn" @click="copyWebhookUrl" :class="{ 'gl-copy-success': copied }">
                  <svg v-if="!copied" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  {{ copied ? 'Copied!' : 'Copy' }}
                </button>
              </div>
              <div class="gl-webhook-hint">
                For local testing use ngrok: <code class="gl-code">ngrok http 8082</code>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2 -->
        <div class="gl-step">
          <div class="gl-step-num">2</div>
          <div class="gl-step-body">
            <div class="gl-step-title">Map Loyverse Stores to Branches</div>
            <p class="gl-step-desc">
              Each Loyverse store must be linked to a GetLokal branch. Orders will route to the correct branch automatically.
              Find the Loyverse Store ID in your Back Office URL when viewing a store.
            </p>
          </div>
        </div>
      </div>

      <!-- Mappings card -->
      <div class="gl-mappings-card">
        <div class="gl-mappings-header">
          <div>
            <h2 class="gl-mappings-title">Store Mappings</h2>
            <p class="gl-mappings-sub">{{ mappings.length }} mapping{{ mappings.length !== 1 ? 's' : '' }} configured</p>
          </div>
          <button class="gl-btn-add" @click="openAddModal">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Add Mapping
          </button>
        </div>

        <!-- Loading -->
        <div v-if="loading" class="gl-skel-rows">
          <div v-for="i in 2" :key="i" class="gl-skel-row-item">
            <div style="flex:1">
              <div class="gl-skel gl-skel-line" style="width:40%;margin-bottom:7px"></div>
              <div class="gl-skel gl-skel-line" style="width:65%;height:11px"></div>
            </div>
            <div class="gl-skel" style="width:60px;height:22px;border-radius:20px"></div>
            <div class="gl-skel" style="width:32px;height:32px;border-radius:8px"></div>
          </div>
        </div>

        <!-- Empty -->
        <div v-else-if="!mappings.length" class="gl-mappings-empty">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
          <span>No store mappings yet. Click <strong>Add Mapping</strong> to get started.</span>
        </div>

        <!-- Table -->
        <div v-else class="gl-table-wrap">
          <table class="gl-table">
            <thead>
              <tr>
                <th>GetLokal Branch</th>
                <th>Loyverse Store ID</th>
                <th>Store Name</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="mapping in mappings" :key="mapping.id">
                <td class="gl-td-name">{{ mapping.outlet?.name || 'Unknown' }}</td>
                <td><code class="gl-code">{{ mapping.loyverse_store_id }}</code></td>
                <td class="gl-td-meta">{{ mapping.loyverse_store_name || '—' }}</td>
                <td>
                  <span class="gl-status-badge" :class="mapping.is_active ? 'gl-badge-active' : 'gl-badge-inactive'">
                    {{ mapping.is_active ? 'Active' : 'Inactive' }}
                  </span>
                </td>
                <td>
                  <button class="gl-btn-delete" @click="deleteMapping(mapping)" :disabled="deletingId === mapping.id">
                    <span v-if="deletingId === mapping.id" class="gl-spinner"></span>
                    <svg v-else width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add Mapping Modal -->
    <div v-if="addModalOpen" class="gl-modal-backdrop" @click.self="addModalOpen = false">
      <div class="gl-modal">
        <div class="gl-modal-header">
          <h3 class="gl-modal-title">Add Store Mapping</h3>
          <button class="gl-modal-close" @click="addModalOpen = false">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <div class="gl-field-group">
            <label class="gl-label">GetLokal Branch <span class="gl-required">*</span></label>
            <div class="gl-select-wrap">
              <select class="gl-select gl-select-full" v-model="newMapping.outlet_id">
                <option value="">Select branch...</option>
                <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
              </select>
              <svg class="gl-select-chevron" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
            </div>
          </div>
          <div class="gl-field-group">
            <label class="gl-label">Loyverse Store ID <span class="gl-required">*</span></label>
            <input type="text" class="gl-input" v-model="newMapping.loyverse_store_id" placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890">
            <div class="gl-hint">Find this in your Loyverse Back Office URL when viewing a store</div>
          </div>
          <div class="gl-field-group" style="margin-bottom:0">
            <label class="gl-label">Store Name <span class="gl-label-optional">optional</span></label>
            <input type="text" class="gl-input" v-model="newMapping.loyverse_store_name" placeholder="e.g., Main Outlet">
          </div>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-ghost" @click="addModalOpen = false">Cancel</button>
          <button class="gl-btn-save" @click="saveMapping" :disabled="saving || !newMapping.outlet_id || !newMapping.loyverse_store_id">
            <span v-if="saving" class="gl-spinner gl-spinner-white"></span>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            Save Mapping
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirm Modal -->
    <div v-if="deleteTarget" class="gl-modal-backdrop" @click.self="deleteTarget = null">
      <div class="gl-modal gl-modal-sm">
        <div class="gl-modal-header">
          <h3 class="gl-modal-title">Remove Mapping</h3>
          <button class="gl-modal-close" @click="deleteTarget = null">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
        <div class="gl-modal-body">
          <p class="gl-confirm-text">Remove the mapping for <strong>{{ deleteTarget?.outlet?.name || 'this store' }}</strong>? Orders from this Loyverse store will no longer route to GetLokal.</p>
        </div>
        <div class="gl-modal-footer">
          <button class="gl-btn-ghost" @click="deleteTarget = null">Cancel</button>
          <button class="gl-btn-danger" @click="confirmDelete" :disabled="deletingId !== null">
            <span v-if="deletingId" class="gl-spinner gl-spinner-white"></span>
            Remove
          </button>
        </div>
      </div>
    </div>

    <!-- Toast -->
    <transition name="gl-toast">
      <div v-if="toastMsg" class="gl-toast" :class="toastType === 'error' ? 'gl-toast-error' : 'gl-toast-success'">
        <svg v-if="toastType !== 'error'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        {{ toastMsg }}
      </div>
    </transition>
  </div>
</template>

<script>
import { ownerLoyverseApi, ownerBranchApi } from '@/services/ownerApi';

export default {
  name: 'LoyverseSettings',
  data() {
    return {
      loading: true,
      saving: false,
      deletingId: null,
      addModalOpen: false,
      deleteTarget: null,
      copied: false,
      mappings: [],
      branches: [],
      settings: { is_configured: false, mapped_stores: 0, today_orders: 0, webhook_url: '' },
      newMapping: { outlet_id: '', loyverse_store_id: '', loyverse_store_name: '' },
      toastMsg: null, toastType: 'success', toastTimer: null,
    };
  },
  computed: {
    webhookUrl() {
      if (this.settings.webhook_url) return this.settings.webhook_url;
      return window.location.origin.replace('3001', '8082') + '/api/v1/webhooks/loyverse';
    }
  },
  mounted() { this.fetchData(); },
  methods: {
    async fetchData() {
      try {
        this.loading = true;
        const [sR, mR, bR] = await Promise.all([
          ownerLoyverseApi.getSettings(),
          ownerLoyverseApi.getStoreMappings(),
          ownerBranchApi.list()
        ]);
        this.settings = sR.data.data;
        this.mappings = mR.data.data;
        this.branches = bR.data.data;
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    copyWebhookUrl() {
      navigator.clipboard.writeText(this.webhookUrl).then(() => {
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      }).catch(() => {
        this.$refs.webhookInput.select();
        document.execCommand('copy');
        this.copied = true;
        setTimeout(() => { this.copied = false; }, 2000);
      });
    },
    openAddModal() {
      this.newMapping = { outlet_id: '', loyverse_store_id: '', loyverse_store_name: '' };
      this.addModalOpen = true;
    },
    async saveMapping() {
      try {
        this.saving = true;
        await ownerLoyverseApi.saveStoreMapping(this.newMapping);
        this.addModalOpen = false;
        this.showToast('Store mapping saved.');
        this.fetchData();
      } catch (e) {
        this.showToast(e.response?.data?.message || 'Failed to save mapping', 'error');
      } finally { this.saving = false; }
    },
    deleteMapping(mapping) { this.deleteTarget = mapping; },
    async confirmDelete() {
      if (!this.deleteTarget) return;
      try {
        this.deletingId = this.deleteTarget.id;
        await ownerLoyverseApi.deleteStoreMapping(this.deleteTarget.id);
        this.deleteTarget = null;
        this.showToast('Mapping removed.');
        this.fetchData();
      } catch (e) {
        this.showToast('Failed to delete mapping', 'error');
      } finally { this.deletingId = null; }
    },
    showToast(msg, type = 'success') {
      this.toastMsg = msg; this.toastType = type;
      clearTimeout(this.toastTimer);
      this.toastTimer = setTimeout(() => { this.toastMsg = null; }, 3200);
    }
  }
};
</script>

<style scoped>
.gl-page { padding: 28px 32px; max-width: 960px; }
.gl-page-header { margin-bottom: 20px; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #1a2e2b; margin: 0 0 4px; letter-spacing: -0.3px; }
.gl-breadcrumb { font-size: 12px; color: #8a9e9a; display: flex; align-items: center; gap: 6px; }
.gl-breadcrumb a { color: #8a9e9a; text-decoration: none; }
.gl-breadcrumb a:hover { color: #900C27; }
.gl-breadcrumb-sep { opacity: .5; }

/* ── Status banner ── */
.gl-status-banner { display: flex; align-items: center; gap: 10px; padding: 12px 16px; border-radius: 10px; font-size: 13px; margin-bottom: 24px; }
.gl-banner-ok { background: #e8f5f0; border: 1px solid #b8ddd1; color: #1a5248; }
.gl-banner-warn { background: #fffceb; border: 1px solid #f0e088; color: #7a6520; }
.gl-banner-icon { flex-shrink: 0; display: flex; align-items: center; }

/* ── Layout ── */
.gl-settings-layout { display: flex; flex-direction: column; gap: 20px; }

/* ── Setup card ── */
.gl-setup-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 24px; }
.gl-setup-header { display: flex; align-items: center; gap: 10px; margin-bottom: 22px; padding-bottom: 16px; border-bottom: 1px solid #eef0ef; }
.gl-setup-icon { width: 36px; height: 36px; border-radius: 10px; background: #e0f0ec; color: #900C27; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-setup-title { font-size: 15px; font-weight: 700; color: #1a2e2b; margin: 0; }

.gl-step { display: flex; gap: 16px; margin-bottom: 20px; }
.gl-step:last-child { margin-bottom: 0; }
.gl-step-num { width: 28px; height: 28px; border-radius: 50%; background: #900C27; color: #fff; font-size: 13px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 1px; }
.gl-step-body { flex: 1; min-width: 0; }
.gl-step-title { font-size: 14px; font-weight: 700; color: #1a2e2b; margin-bottom: 8px; }
.gl-step-desc { font-size: 13px; color: #5a7470; line-height: 1.5; margin: 0; }
.gl-step-list { padding-left: 18px; margin: 0 0 14px; }
.gl-step-list li { font-size: 13px; color: #4a6360; line-height: 1.7; }
.gl-link { color: #900C27; font-weight: 500; text-decoration: none; }
.gl-link:hover { text-decoration: underline; }

.gl-webhook-block { margin-top: 12px; }
.gl-webhook-label { font-size: 11.5px; font-weight: 600; color: #6b8a85; text-transform: uppercase; letter-spacing: .3px; margin-bottom: 6px; }
.gl-webhook-row { display: flex; gap: 0; }
.gl-webhook-input { flex: 1; padding: 9px 13px; font-size: 12.5px; font-family: 'Menlo', 'Consolas', monospace; color: #1a2e2b; background: #f8faf9; border: 1.5px solid #e0e9e7; border-right: none; border-radius: 8px 0 0 8px; outline: none; }
.gl-copy-btn { display: inline-flex; align-items: center; gap: 6px; padding: 9px 14px; font-size: 12.5px; font-weight: 600; color: #900C27; background: #f0f8f5; border: 1.5px solid #c8e6de; border-radius: 0 8px 8px 0; cursor: pointer; transition: background .15s, color .15s; white-space: nowrap; }
.gl-copy-btn:hover { background: #e0f0ec; }
.gl-copy-success { color: #16a34a !important; background: #e8f5e9 !important; }
.gl-webhook-hint { font-size: 12px; color: #8a9e9a; margin-top: 6px; }
.gl-code { font-family: 'Menlo', 'Consolas', monospace; font-size: 11.5px; background: #f0f3f2; padding: 2px 6px; border-radius: 4px; color: #900C27; }

/* ── Mappings card ── */
.gl-mappings-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; }
.gl-mappings-header { display: flex; align-items: flex-start; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #f0f3f2; }
.gl-mappings-title { font-size: 15px; font-weight: 700; color: #1a2e2b; margin: 0 0 3px; }
.gl-mappings-sub { font-size: 12px; color: #8a9e9a; margin: 0; }
.gl-btn-add { display: inline-flex; align-items: center; gap: 7px; padding: 9px 16px; font-size: 13px; font-weight: 600; background: #900C27; color: #fff; border: none; border-radius: 8px; cursor: pointer; transition: background .15s; }
.gl-btn-add:hover { background: #1a5248; }

.gl-mappings-empty { display: flex; align-items: center; gap: 10px; color: #a0b5b0; font-size: 13px; padding: 36px 24px; }

.gl-table-wrap { overflow-x: auto; }
.gl-table { width: 100%; border-collapse: collapse; }
.gl-table thead th { font-size: 11.5px; font-weight: 700; color: #6b8a85; text-transform: uppercase; letter-spacing: .3px; padding: 11px 20px; background: #f8faf9; border-bottom: 1px solid #eef0ef; white-space: nowrap; }
.gl-table tbody tr { border-bottom: 1px solid #f5f7f6; transition: background .12s; }
.gl-table tbody tr:last-child { border-bottom: none; }
.gl-table tbody tr:hover { background: #f9fbfa; }
.gl-table tbody td { padding: 14px 20px; font-size: 13.5px; color: #2a3d3a; }
.gl-td-name { font-weight: 600; color: #1a2e2b; }
.gl-td-meta { color: #6b7f7b; }

.gl-status-badge { font-size: 11.5px; font-weight: 600; padding: 3px 10px; border-radius: 20px; }
.gl-badge-active { background: #e0f0ec; color: #1a5248; }
.gl-badge-inactive { background: #f0f3f2; color: #6b7f7b; }

.gl-btn-delete { display: flex; align-items: center; justify-content: center; width: 32px; height: 32px; border-radius: 8px; background: #fff; border: 1.5px solid #f0d5d5; color: #dc2626; cursor: pointer; transition: background .12s; }
.gl-btn-delete:hover:not(:disabled) { background: #fef2f2; }
.gl-btn-delete:disabled { opacity: .4; cursor: not-allowed; }

/* ── Skeleton ── */
.gl-skel-rows { padding: 8px 0; }
.gl-skel-row-item { display: flex; align-items: center; gap: 16px; padding: 16px 24px; border-bottom: 1px solid #f3f5f4; }
.gl-skel { background: linear-gradient(90deg, #f0f2f1 25%, #e5e8e7 50%, #f0f2f1 75%); background-size: 200% 100%; animation: gl-shimmer 1.4s infinite; border-radius: 6px; }
.gl-skel-line { height: 13px; }
@keyframes gl-shimmer { to { background-position: -200% 0; } }

/* ── Fields ── */
.gl-field-group { margin-bottom: 18px; }
.gl-label { display: block; font-size: 12px; font-weight: 600; color: #3d5450; margin-bottom: 7px; letter-spacing: .2px; text-transform: uppercase; }
.gl-required { color: #e05252; }
.gl-label-optional { font-size: 11px; font-weight: 400; color: #9db5b0; text-transform: none; margin-left: 4px; }
.gl-input { width: 100%; padding: 10px 13px; font-size: 13.5px; color: #1a2e2b; background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px; outline: none; transition: border-color .15s; font-family: inherit; box-sizing: border-box; }
.gl-input:focus { border-color: #900C27; background: #fff; box-shadow: 0 0 0 3px rgba(144,12,39,.08); }
.gl-hint { font-size: 11.5px; color: #8a9e9a; margin-top: 5px; }
.gl-select-wrap { position: relative; }
.gl-select { appearance: none; padding: 10px 30px 10px 13px; font-size: 13.5px; color: #1a2e2b; background: #f8faf9; border: 1.5px solid #e0e9e7; border-radius: 8px; outline: none; cursor: pointer; font-family: inherit; }
.gl-select-full { width: 100%; box-sizing: border-box; }
.gl-select:focus { border-color: #900C27; background: #fff; }
.gl-select-chevron { position: absolute; right: 11px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #8a9e9a; }

/* ── Buttons ── */
.gl-btn-save { display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px; font-size: 13.5px; font-weight: 600; background: #900C27; color: #fff; border: none; border-radius: 8px; cursor: pointer; transition: background .15s; }
.gl-btn-save:hover:not(:disabled) { background: #1a5248; }
.gl-btn-save:disabled { opacity: .55; cursor: not-allowed; }
.gl-btn-ghost { display: inline-flex; align-items: center; gap: 7px; padding: 10px 18px; font-size: 13px; font-weight: 500; background: #fff; color: #4a6360; border: 1.5px solid #d4dedd; border-radius: 8px; cursor: pointer; }
.gl-btn-ghost:hover { border-color: #b0c4c0; background: #f8faf9; }
.gl-btn-danger { display: inline-flex; align-items: center; gap: 7px; padding: 10px 20px; font-size: 13.5px; font-weight: 600; background: #dc2626; color: #fff; border: none; border-radius: 8px; cursor: pointer; }
.gl-btn-danger:hover:not(:disabled) { background: #b91c1c; }
.gl-btn-danger:disabled { opacity: .55; cursor: not-allowed; }

/* ── Modal ── */
.gl-modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 24px; }
.gl-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 480px; box-shadow: 0 24px 60px rgba(0,0,0,.18); animation: gl-modal-in .2s ease; }
.gl-modal-sm { max-width: 420px; }
@keyframes gl-modal-in { from { opacity: 0; transform: scale(.96) translateY(8px); } to { opacity: 1; } }
.gl-modal-header { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eef0ef; }
.gl-modal-title { font-size: 16px; font-weight: 700; color: #1a2e2b; margin: 0; }
.gl-modal-close { display: flex; align-items: center; justify-content: center; width: 30px; height: 30px; border-radius: 8px; background: #f8faf9; border: none; color: #6b7f7b; cursor: pointer; }
.gl-modal-close:hover { background: #eef0ef; }
.gl-modal-body { padding: 20px 24px; }
.gl-modal-footer { display: flex; gap: 10px; justify-content: flex-end; padding: 16px 24px; border-top: 1px solid #eef0ef; }
.gl-confirm-text { font-size: 13.5px; color: #4a6360; line-height: 1.5; margin: 0; }

/* ── Spinner ── */
.gl-spinner { display: inline-block; width: 12px; height: 12px; border: 2px solid rgba(144,12,39,.25); border-top-color: #900C27; border-radius: 50%; animation: gl-spin .65s linear infinite; }
.gl-spinner-white { border-color: rgba(255,255,255,.3); border-top-color: #fff; }
@keyframes gl-spin { to { transform: rotate(360deg); } }

/* ── Toast ── */
.gl-toast { position: fixed; bottom: 28px; right: 28px; z-index: 2000; display: flex; align-items: center; gap: 9px; padding: 12px 18px; border-radius: 10px; font-size: 13.5px; font-weight: 500; box-shadow: 0 8px 24px rgba(0,0,0,.14); }
.gl-toast-success { background: #1a5248; color: #fff; }
.gl-toast-error { background: #dc2626; color: #fff; }
.gl-toast-enter-active, .gl-toast-leave-active { transition: all .3s ease; }
.gl-toast-enter-from, .gl-toast-leave-to { opacity: 0; transform: translateY(12px) scale(.95); }

/* ── Mobile ── */
@media (max-width: 767px) {
  .gl-page { padding: 14px 16px 40px; }
  .gl-page-title { font-size: 18px; }
  .gl-breadcrumb { display: none; }
  .gl-page-header { margin-bottom: 14px; }

  /* Status banner */
  .gl-status-banner { padding: 11px 13px; font-size: 12.5px; margin-bottom: 16px; align-items: flex-start; }

  /* Setup card */
  .gl-setup-card { padding: 16px; }
  .gl-setup-header { margin-bottom: 16px; padding-bottom: 12px; }
  .gl-step { gap: 12px; margin-bottom: 18px; }
  .gl-step-list { padding-left: 16px; }
  .gl-step-list li { font-size: 12.5px; }
  .gl-webhook-hint { font-size: 11.5px; word-break: break-all; }

  /* Webhook row: stack copy button below URL */
  .gl-webhook-row { flex-direction: column; gap: 8px; }
  .gl-webhook-input { border-right: 1.5px solid #e0e9e7; border-radius: 8px; width: 100%; box-sizing: border-box; font-size: 11.5px; }
  .gl-copy-btn { border-radius: 8px; width: 100%; justify-content: center; padding: 9px 14px; }

  /* Mappings card header: stack title + button */
  .gl-mappings-header { padding: 14px 16px 12px; flex-direction: column; align-items: stretch; gap: 10px; }
  .gl-mappings-title { font-size: 14px; }
  .gl-btn-add { width: 100%; justify-content: center; padding: 10px 16px; }

  /* Table: min-width + hide Store Name column */
  .gl-table { min-width: 380px; }
  .gl-table thead th:nth-child(3),
  .gl-table tbody td:nth-child(3) { display: none; }
  .gl-table thead th { padding: 10px 14px; font-size: 11px; }
  .gl-table tbody td { padding: 12px 14px; font-size: 13px; }

  /* Skeleton */
  .gl-skel-row-item { padding: 14px 16px; }

  /* Empty state */
  .gl-mappings-empty { padding: 28px 16px; font-size: 12.5px; }

  /* Modal */
  .gl-modal-backdrop { padding: 68px 16px 16px; align-items: center; }
  .gl-modal { border-radius: 16px; max-height: calc(100vh - 84px); overflow-y: auto; }
  .gl-modal-header { padding: 16px 16px 14px; }
  .gl-modal-body { padding: 16px; }
  .gl-modal-footer { padding: 14px 16px; flex-wrap: wrap; }
  .gl-btn-save, .gl-btn-ghost, .gl-btn-danger { flex: 1; justify-content: center; white-space: nowrap; }

  /* Toast */
  .gl-toast { bottom: 16px; right: 16px; left: 16px; }
}
</style>

<style>
body.dark-only .gl-page-title { color: #e0eded; }
body.dark-only .gl-breadcrumb, body.dark-only .gl-breadcrumb a { color: #5a7470; }
body.dark-only .gl-status-banner.gl-banner-ok { background: #1c3530; border-color: #2a4d46; color: #4caf9a; }
body.dark-only .gl-status-banner.gl-banner-warn { background: #2a2710; border-color: #4a3f10; color: #c9a83c; }
body.dark-only .gl-setup-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-setup-header { border-bottom-color: #2a3d3a; }
body.dark-only .gl-setup-title { color: #e0eded; }
body.dark-only .gl-step-title { color: #e0eded; }
body.dark-only .gl-step-desc, body.dark-only .gl-step-list li { color: #7aada5; }
body.dark-only .gl-webhook-input { background: #16211f; border-color: #2a3d3a; color: #e0eded; }
body.dark-only .gl-copy-btn { background: #1c2f2b; border-color: #2a3d3a; color: #4caf9a; }
body.dark-only .gl-code { background: #16211f; color: #4caf9a; }
body.dark-only .gl-mappings-card { background: #1e2d2b; border-color: #2a3d3a; }
body.dark-only .gl-mappings-header { border-bottom-color: #2a3d3a; }
body.dark-only .gl-mappings-title { color: #e0eded; }
body.dark-only .gl-table thead th { background: #16211f; color: #5a7470; border-bottom-color: #2a3d3a; }
body.dark-only .gl-table tbody tr { border-bottom-color: #222f2d; }
body.dark-only .gl-table tbody tr:hover { background: #1c2f2b; }
body.dark-only .gl-table tbody td { color: #c0d4d0; }
body.dark-only .gl-td-name { color: #e0eded; }
body.dark-only .gl-btn-delete { background: #1e2d2b; border-color: #4a2828; color: #f87171; }
body.dark-only .gl-btn-delete:hover:not(:disabled) { background: #2a1c1c; }
body.dark-only .gl-skel-row-item { border-bottom-color: #243432; }
body.dark-only .gl-skel { background: linear-gradient(90deg, #243432 25%, #2a3d3a 50%, #243432 75%); background-size: 200% 100%; }
body.dark-only .gl-modal { background: #1e2d2b; }
body.dark-only .gl-modal-header { border-bottom-color: #2a3d3a; }
body.dark-only .gl-modal-title { color: #e0eded; }
body.dark-only .gl-modal-close { background: #16211f; color: #7aada5; }
body.dark-only .gl-modal-footer { border-top-color: #2a3d3a; }
body.dark-only .gl-input { background: #16211f; border-color: #2a3d3a; color: #e0eded; }
body.dark-only .gl-input:focus { border-color: #4caf9a; background: #1e2d2b; }
body.dark-only .gl-select { background: #16211f; border-color: #2a3d3a; color: #e0eded; }
body.dark-only .gl-btn-ghost { background: #1e2d2b; border-color: #2a3d3a; color: #8ab5ad; }
body.dark-only .gl-confirm-text { color: #7aada5; }

@media (max-width: 767px) {
  body.dark-only .gl-webhook-input { border-right-color: #2a3d3a; }
}
</style>
